'use client';

import React, { useState, useRef, useEffect, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import styles from './DropdownMenu.module.css';

export interface DropdownMenuProps {
    trigger: ReactNode;
    children: ReactNode;
    position?: 'top' | 'bottom-left' | 'bottom-right' | 'top-left' | 'top-right';
    offset?: number;
    leftOffset?: number;
    style?: React.CSSProperties;
    portalStyle?: React.CSSProperties;
    closeOnContentClick?: boolean;
    onClose?: () => void;
}

const DropdownMenu: React.FC<DropdownMenuProps> = ({
    trigger,
    children,
    position = 'bottom-left',
    offset = 10,
    leftOffset = 0,
    style,
    portalStyle,
    closeOnContentClick = true,
    onClose
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isPositioned, setIsPositioned] = useState(false);
    const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
    const dropdownRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLDivElement>(null);

    const toggleDropdown = () => {
        setIsPositioned(false);
        setIsOpen(prev => !prev);
    };

    const closeDropdown = () => {
        setIsOpen(prev => {
            if (prev) onClose?.();
            return false;
        });
        setIsPositioned(false);
    };

    useEffect(() => {
        if (isOpen && triggerRef.current && dropdownRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            const dropdown = dropdownRef.current.getBoundingClientRect();

            let top = 0;
            let left = 0;

            // Initial positioning
            switch (position) {
            case 'bottom-left':
                top = rect.top + rect.height + offset;
                left = rect.left;
                break;
            case 'bottom-right':
                top = rect.top + rect.height + offset;
                left = rect.right - dropdown.width;
                break;
            case 'top-left':
                top = rect.top - dropdown.height - offset;
                left = rect.left;
                break;
            case 'top-right':
                top = rect.top - dropdown.height - offset;
                left = rect.right - dropdown.width;
                break;
            case 'top':
                top = rect.top - offset;
                left = rect.left + (rect.width / 2) - 80;
                break;
            }

            // Check screen boundaries
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;

            // Adjust horizontal position
            if (left + dropdown.width > viewportWidth) {
                left = viewportWidth - dropdown.width - 10;
            }
            if (left < 0) {
                left = 10;
            }

            // Adjust vertical position
            if (top + dropdown.height > viewportHeight) {
                // Change direction if doesn't fit below
                if (position.startsWith('bottom')) {
                    top = rect.top - dropdown.height - offset;
                } else {
                    top = viewportHeight - dropdown.height - 10;
                }
            }
            if (top < 0) {
                // Change direction if doesn't fit above
                if (position.startsWith('top')) {
                    top = rect.top + rect.height + offset;
                } else {
                    top = 10;
                }
            }

            setMenuPosition({ top, left: left + leftOffset });
            setIsPositioned(true);
        }
    }, [isOpen, position, offset]);


    useEffect(() => {
        const handleScroll = (e: Event) => {
            if (isOpen) {
                // Don't close when scrolling inside the dropdown portal
                if (dropdownRef.current && dropdownRef.current.contains(e.target as Node)) {
                    return;
                }
                closeDropdown();
            }
        };

        if (isOpen) {
            window.addEventListener('scroll', handleScroll, true);
        }

        return () => {
            window.removeEventListener('scroll', handleScroll, true);
        };
    }, [isOpen]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node) &&
                triggerRef.current &&
                !triggerRef.current.contains(event.target as Node)
            ) {
                closeDropdown();
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    useEffect(() => {
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                closeDropdown();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, [isOpen]);

    return (
        <div className={styles.dropdown} style={style || {}}>
            <div
                className={styles.trigger}
                onClick={toggleDropdown}
                ref={triggerRef}
            >
                {trigger}
            </div>

            {isOpen && createPortal(
                <div
                    className={styles.menuPortal}
                    style={{
                        position: 'fixed',
                        top: `${menuPosition.top}px`,
                        left: `${menuPosition.left}px`,
                        zIndex: 1000,
                        visibility: isPositioned ? 'visible' : 'hidden',
                        ...portalStyle
                    }}
                    ref={dropdownRef}
                >
                    <div className={styles.menuContent} onClick={closeOnContentClick ? closeDropdown : undefined}>
                        {children}
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default DropdownMenu;
